import React, {
  useEffect,
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
} from 'react';
import useFilteredTags, {
  FilteredTags,
} from '~/App/Modules/Tags/Components/TagFilter/hooks/useFilteredTags';
import {
  TestimonialRespondentsMedia,
  TestimonialRespondentStatus,
} from '~/types/models/testimonial';
import { PaginationMetadata, UsePagination } from '~/react-hooks/usePagination';
import useCurrentUser from '~/react-hooks/use-current-user';
import { useQueryClient } from 'react-query';
import { usePublishPageContext } from '~/App/Modules/Testimonials/Pages/Publish/Editor/Contexts/PublishPageContext';
import { ModalTabs } from '~/App/Modules/Testimonials/Pages/Publish/Editor/Containers/PublishModal/types';

import { testimonialRespondentsMediaQueryKeys } from '~/App/Modules/MyLibrary/Api/Testimonials';

import { useTestimonialMediaPagination } from '~/App/Api/Testimonial';

import {
  useUserAssetsPagination,
  userAssetsQueryKeys,
  UserAsset,
} from '~/App/Modules/MyLibrary/Api/UserAsset';
import {
  userWebAssetsQueryKeys,
  useUserWebAssetsPagination,
} from '~/App/Modules/MyLibrary/Api/UserWebAsset';
import { UserWebAsset } from '~/types/models';
import useStateObject from '~/react-hooks/use-state-object';
import { partial } from 'lodash';
import { MAX_TESTIMONIALS } from '../../constants';
import { usePublishEditorState } from './PublishEditorContext';
import { PublishableType } from '~/types/models/publishable';

export type SelectedAllTabsStatus = Record<ModalTabs, boolean>;

export interface PublishSelectModalContextProps extends FilteredTags {
  selectedIds: string[];
  isDirty: boolean;
  done: () => void;
  selectAllIds: (type: ModalTabs) => void;
  addSelectedId: (id: string) => void;
  modalSearchString: string;
  updateSelectedAllIdsForKey: (key: ModalTabs, value: boolean) => void;
  setModalSearchString: (searchString: string) => void;
  removeSelectedId: (id: string) => void;
  removeAllSelectedIds: (type: ModalTabs) => void;
  invalidateAllQueries: () => void;
  invalidateTestimonialRespondentQueries: () => void;
  invalidateUserAssetsQueries: () => void;
  invalidateWebAssetsQueries: () => void;
  setPerPage: (pages: number) => void;
  resetTestimonialRespondentMediaQueryKeys: () => void;
  resetUserAssetQueryKeys: () => void;
  resetUserWebAssetQueryKeys: () => void;
  resetSelectedAllIdsToDefault: () => void;
  testimonialsPagination?: UsePagination<
    TestimonialRespondentsMedia,
    PaginationMetadata
  >;
  userAssetsPagination?: UsePagination<UserAsset, PaginationMetadata>;
  webAssetsPagination?: UsePagination<UserWebAsset, PaginationMetadata>;
  selectedTestimonialsPagination?: UsePagination<
    TestimonialRespondentsMedia,
    PaginationMetadata
  >;
  selectedUserAssetsPagination?: UsePagination<UserAsset, PaginationMetadata>;
  selectedWebAssetsPagination?: UsePagination<UserWebAsset, PaginationMetadata>;
  selectedAllIds: SelectedAllTabsStatus;
  isItemSelectDisabled: (id: string) => boolean;
}

const defaultSelectedAllIds = {
  [ModalTabs.All]: false,
  [ModalTabs.Uploads]: false,
  [ModalTabs.Responses]: false,
  [ModalTabs.Social]: false,
};

function getUuidArray(resources: Array<{ uuid: string }>): string[] {
  return resources.map((resource) => resource.uuid);
}

export interface SelectModalState {
  selectedIds: string[];
}

const PublishSelectModalContext = createContext<
  PublishSelectModalContextProps | undefined
>(undefined);

const PublishSelectModalContextProvider: React.FC = ({ children }) => {
  const [perPage, setPerPage] = useState<number>(16);
  const [modalSearchString, setModalSearchString] = useState<string>('');
  const [selectedAllIds, setSelectedAllIds] = useState<SelectedAllTabsStatus>(
    defaultSelectedAllIds
  );

  const user = useCurrentUser();

  const filteredTags = useFilteredTags(user.id);

  const queryClient = useQueryClient();

  const publishContext = usePublishPageContext();

  const [{ type: publishableType }] = usePublishEditorState();

  const savedPublishableResources = useMemo(
    () => publishContext?.publish?.publishable_resources ?? [],
    [publishContext?.publish?.publishable_resources]
  );

  const savedIds = useMemo(
    () => getUuidArray(savedPublishableResources),
    [savedPublishableResources]
  );

  const [{ selectedIds = [] }, { isDirty, set, done }] =
    useStateObject<SelectModalState>(() => ({
      selectedIds: getUuidArray(savedPublishableResources),
    }));

  const setSelectedIds = useCallback(partial(set, 'selectedIds'), [set]);

  // this to reset all select all buttons in modal
  const resetSelectedAllIdsToDefault = useCallback(() => {
    setSelectedAllIds(defaultSelectedAllIds);
  }, [setSelectedAllIds]);

  const updateSelectedAllIdsForKey = useCallback(
    (type: ModalTabs, value: boolean) => {
      setSelectedAllIds((prevState) => ({
        ...prevState,
        [type]: value,
      }));
    },
    [setSelectedAllIds]
  );

  const invalidateTestimonialRespondentQueries = useCallback(() => {
    void queryClient.invalidateQueries(
      testimonialRespondentsMediaQueryKeys.lists()
    );
  }, [queryClient]);

  const invalidateUserAssetsQueries = useCallback(() => {
    void queryClient.invalidateQueries(userAssetsQueryKeys.lists());
  }, [queryClient]);

  const invalidateWebAssetsQueries = useCallback(() => {
    void queryClient.invalidateQueries(userWebAssetsQueryKeys.lists());
  }, [queryClient]);

  const invalidateAllQueries = useCallback(() => {
    void invalidateTestimonialRespondentQueries();
    void invalidateUserAssetsQueries();
    void invalidateWebAssetsQueries();
  }, [
    invalidateTestimonialRespondentQueries,
    invalidateUserAssetsQueries,
    invalidateWebAssetsQueries,
  ]);

  const resetTestimonialRespondentMediaQueryKeys = useCallback(() => {
    void queryClient.resetQueries(testimonialRespondentsMediaQueryKeys.lists());
  }, [queryClient]);

  const resetUserAssetQueryKeys = useCallback(() => {
    void queryClient.resetQueries(userAssetsQueryKeys.lists());
  }, [queryClient]);

  const resetUserWebAssetQueryKeys = useCallback(() => {
    void queryClient.resetQueries(userWebAssetsQueryKeys.lists());
  }, [queryClient]);

  const filteredTagIds = filteredTags.getFilteredTagIds();

  const testimonialsPagination = useTestimonialMediaPagination({
    infinite: true,
    key: testimonialRespondentsMediaQueryKeys.lists(),
    perPage,
    requestParams: {
      tag_ids: filteredTagIds,
      search_string: modalSearchString,
      filter_out_publishable_resources_ids: savedIds,
      status: TestimonialRespondentStatus.Consented,
    },
  });

  const selectedTestimonialsPagination = useTestimonialMediaPagination({
    infinite: true,
    key: testimonialRespondentsMediaQueryKeys.lists(),
    perPage,
    requestParams: {
      tag_ids: filteredTagIds,
      search_string: modalSearchString,
      filter_in_publishable_resources_ids: savedIds,
      status: TestimonialRespondentStatus.Consented,
    },
  });

  const userAssetsPagination = useUserAssetsPagination({
    infinite: true,
    key: userAssetsQueryKeys.lists(),
    perPage,
    requestParams: {
      tag_ids: filteredTagIds,
      search_string: modalSearchString,
      filter_out_publishable_resources_ids: savedIds,
    },
  });

  const selectedUserAssetsPagination = useUserAssetsPagination({
    infinite: true,
    key: userAssetsQueryKeys.lists(),
    perPage,
    requestParams: {
      tag_ids: filteredTagIds,
      search_string: modalSearchString,
      filter_in_publishable_resources_ids: savedIds,
    },
  });

  const webAssetsPagination = useUserWebAssetsPagination({
    infinite: true,
    key: userWebAssetsQueryKeys.lists(),
    perPage,
    requestParams: {
      tag_ids: filteredTagIds,
      search_string: modalSearchString,
      filter_out_publishable_resources_ids: savedIds,
    },
  });

  const selectedWebAssetsPagination = useUserWebAssetsPagination({
    infinite: true,
    key: userWebAssetsQueryKeys.lists(),
    perPage,
    requestParams: {
      tag_ids: filteredTagIds,
      search_string: modalSearchString,
      filter_in_publishable_resources_ids: savedIds,
    },
  });

  const addSelectedId = useCallback(
    (id: string) => {
      setSelectedIds((prevIds) => {
        return [...(prevIds ?? []), id];
      });
      resetSelectedAllIdsToDefault();
    },
    [setSelectedIds]
  );

  const removeSelectedId = useCallback(
    (id: string) => {
      setSelectedIds((prevIds) => {
        return (prevIds ?? []).filter((prevId) => prevId !== id);
      });
      resetSelectedAllIdsToDefault();
    },
    [setSelectedIds]
  );

  const removeAllSelectedIds = useCallback(
    (type: ModalTabs) => {
      if (type === ModalTabs.All) {
        setSelectedIds([]);
        return;
      }
      setSelectedIds((prevIds) => {
        if (type === ModalTabs.Responses) {
          const testimonialIds = getUuidArray(testimonialsPagination[0]);
          const selectedTestimonialIds = getUuidArray(
            selectedTestimonialsPagination[0]
          );
          return prevIds?.filter(
            (id) => ![...testimonialIds, ...selectedTestimonialIds].includes(id)
          );
        } else if (type === ModalTabs.Social) {
          const webAssetsIds = getUuidArray(webAssetsPagination[0]);
          const selectedWebAssetsIds = getUuidArray(
            selectedWebAssetsPagination[0]
          );
          return prevIds?.filter(
            (id) => ![...webAssetsIds, ...selectedWebAssetsIds].includes(id)
          );
        } else if (type === ModalTabs.Uploads) {
          const userAssetsIds = getUuidArray(userAssetsPagination[0]);
          const selectedUserAssetsIds = getUuidArray(
            selectedUserAssetsPagination[0]
          );
          return prevIds?.filter(
            (id) => ![...userAssetsIds, ...selectedUserAssetsIds].includes(id)
          );
        }
      });
    },
    [
      setSelectedIds,
      getUuidArray,
      userAssetsPagination,
      testimonialsPagination,
      selectedUserAssetsPagination,
      selectedTestimonialsPagination,
      webAssetsPagination,
      selectedWebAssetsPagination,
    ]
  );

  const getIdsForType = useCallback(
    (type: ModalTabs) => {
      const selectedIds: string[] = [];
      const ids: string[] = [];

      const userAssetsIds = getUuidArray(userAssetsPagination[0]);
      const testimonialIds = getUuidArray(
        testimonialsPagination[0].filter((response) => !response.is_locked)
      );
      const webAssetsIds = getUuidArray(webAssetsPagination[0]);

      const selectedUserAssetsIds = getUuidArray(
        selectedUserAssetsPagination[0]
      );
      const selectedTestimonialIds = getUuidArray(
        selectedTestimonialsPagination[0].filter(
          (response) => !response.is_locked
        )
      );
      const selectedWebAssetsIds = getUuidArray(selectedWebAssetsPagination[0]);

      if (type === ModalTabs.All) {
        selectedIds.push(
          ...selectedTestimonialIds,
          ...selectedUserAssetsIds,
          ...selectedWebAssetsIds
        );
        ids.push(...testimonialIds, ...userAssetsIds, ...webAssetsIds);
      } else if (type === ModalTabs.Responses) {
        selectedIds.push(...selectedTestimonialIds);
        ids.push(...testimonialIds);
      } else if (type === ModalTabs.Social) {
        selectedIds.push(...selectedWebAssetsIds);
        ids.push(...webAssetsIds);
      } else if (type === ModalTabs.Uploads) {
        selectedIds.push(...selectedUserAssetsIds);
        ids.push(...userAssetsIds);
      }

      return [...selectedIds, ...ids];
    },
    [
      userAssetsPagination,
      testimonialsPagination,
      webAssetsPagination,
      selectedUserAssetsPagination,
      selectedTestimonialsPagination,
      selectedWebAssetsPagination,
    ]
  );

  const selectAllIds = useCallback(
    (type: ModalTabs) => {
      setSelectedIds((prevIds) => {
        const selected = Array.from(
          new Set([...(prevIds ?? []), ...getIdsForType(type)])
        );

        if (publishableType === PublishableType.Slider) {
          return selected.slice(0, MAX_TESTIMONIALS);
        }

        return selected;
      });
    },
    [
      publishableType,
      setSelectedIds,
      userAssetsPagination,
      testimonialsPagination,
      webAssetsPagination,
      selectedUserAssetsPagination,
      selectedTestimonialsPagination,
      selectedWebAssetsPagination,
    ]
  );

  const isItemSelectDisabled = useCallback(
    (id: string) => {
      if (selectedIds.includes(id)) {
        return false;
      }

      // Make locked responses to be unselectable
      if (
        testimonialsPagination[0]
          .filter((response) => response.is_locked)
          .map((response) => response.uuid)
          .includes(id)
      ) {
        return true;
      }

      if (publishableType === PublishableType.WallOfLove) {
        return false;
      }
      return selectedIds.length >= MAX_TESTIMONIALS;
    },
    [publishableType, selectedIds, testimonialsPagination]
  );

  const contextValue: PublishSelectModalContextProps = useMemo(
    () => ({
      selectedIds,
      addSelectedId,
      removeSelectedId,
      removeAllSelectedIds,
      selectAllIds,
      modalSearchString,
      setModalSearchString,
      invalidateAllQueries,
      invalidateTestimonialRespondentQueries,
      invalidateUserAssetsQueries,
      invalidateWebAssetsQueries,
      setPerPage,
      testimonialsPagination,
      userAssetsPagination,
      webAssetsPagination,
      selectedTestimonialsPagination,
      selectedUserAssetsPagination,
      selectedWebAssetsPagination,
      resetUserAssetQueryKeys,
      resetUserWebAssetQueryKeys,
      resetTestimonialRespondentMediaQueryKeys,
      selectedAllIds,
      updateSelectedAllIdsForKey,
      resetSelectedAllIdsToDefault,
      isItemSelectDisabled,
      ...filteredTags,
      isDirty,
      done,
    }),
    [
      selectedIds,
      isDirty,
      done,
      addSelectedId,
      removeSelectedId,
      removeAllSelectedIds,
      selectAllIds,
      modalSearchString,
      setModalSearchString,
      invalidateAllQueries,
      invalidateTestimonialRespondentQueries,
      invalidateUserAssetsQueries,
      invalidateWebAssetsQueries,
      setPerPage,
      testimonialsPagination,
      userAssetsPagination,
      webAssetsPagination,
      selectedTestimonialsPagination,
      selectedUserAssetsPagination,
      selectedWebAssetsPagination,
      resetUserAssetQueryKeys,
      resetUserWebAssetQueryKeys,
      resetTestimonialRespondentMediaQueryKeys,
      selectedAllIds,
      updateSelectedAllIdsForKey,
      resetSelectedAllIdsToDefault,
      filteredTags,
      isItemSelectDisabled,
    ]
  );

  useEffect(() => {
    /**
     * Restart from page 1 everytime the tags are changed
     *
     * Everytime infinite query executes the page will start at the next page.
     * We have to restart it to page 1 that way when we go back to empty filter
     * it will not try and load all the pages.
     */
    invalidateAllQueries();
  }, [filteredTags.filteredTags]);

  return (
    <PublishSelectModalContext.Provider value={contextValue}>
      {children}
    </PublishSelectModalContext.Provider>
  );
};

const usePublishSelectModalContext = ():
  | PublishSelectModalContextProps
  | null
  | undefined => {
  return useContext(PublishSelectModalContext);
};

export {
  PublishSelectModalContext,
  PublishSelectModalContextProvider,
  usePublishSelectModalContext,
};
